/**
 * Demo/QA traffic hygiene (MOVMO-478). Launch a demo with `?mv_internal=1` and
 * the whole embedded session is flagged internal so it's excluded from
 * partner-facing analytics KPIs: the embedded Express Checkout button
 * (`@movmo/express-checkout-button` >= 1.8.0) reads the param at init, stamps
 * `is_internal` on its own scoped events, and forwards `mv_internal=1` into the
 * flights-ui iframe so the funnel joins one flagged session.
 *
 * The button reads the LIVE URL at mount (on the checkout page), but this SPA
 * reconstructs URLs during navigation and drops query params — so we persist the
 * flag in sessionStorage (mirroring `movmo_demo_mock_mode` for `?mock=true`) and
 * re-assert it onto the URL on every route change.
 */
const INTERNAL_KEY = "movmo_demo_internal";

const truthy = (v: string | null): boolean => v === "1" || v === "true";

/** True when this tab is running a flagged internal/QA/demo session. */
export function isInternalDemo(): boolean {
    try {
        const params = new URLSearchParams(window.location.search);
        if (truthy(params.get("mv_internal"))) {
            window.sessionStorage.setItem(INTERNAL_KEY, "1");
            return true;
        }
        return window.sessionStorage.getItem(INTERNAL_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * Re-assert `mv_internal=1` on the current URL if the demo is flagged but the
 * param was dropped by SPA navigation, so the Express Checkout button sees it at
 * mount. No-op when not flagged or already present.
 */
export function ensureInternalFlagInUrl(): void {
    try {
        if (!isInternalDemo()) return;
        const url = new URL(window.location.href);
        if (truthy(url.searchParams.get("mv_internal"))) return;
        url.searchParams.set("mv_internal", "1");
        window.history.replaceState(window.history.state, "", url.toString());
    } catch {
        /* no-op — demo hygiene must never break the mock site */
    }
}
